import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getStrings } from '../constants/strings';
import { sendContactEmail, validateEmail, validatePhone } from '../utils/emailService';
import { logger } from '../utils/logger';

interface ContactFormData {
    name: string;
    email: string;
    company: string;
    phone: string;
    message: string;
}

interface ContactUsPageProps {
    onBack?: () => void;
}

export default function ContactUsPage({ onBack }: ContactUsPageProps) {
    const { language } = useLanguage();
    const strings = getStrings(language);
    const navigate = useNavigate();
    const location = useLocation();
    
    const [formData, setFormData] = useState<ContactFormData>({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: ''
    });
    
    const [errors, setErrors] = useState<Partial<ContactFormData>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const validateForm = (): boolean => {
        const newErrors: Partial<ContactFormData> = {};
        
        if (!formData.name.trim()) {
            newErrors.name = strings.REQUIRED_FIELD_ERROR;
        }
        
        if (!formData.email.trim()) {
            newErrors.email = strings.REQUIRED_FIELD_ERROR;
        } else if (!validateEmail(formData.email)) {
            newErrors.email = strings.INVALID_EMAIL_ERROR;
        }
        
        if (!formData.message.trim()) {
            newErrors.message = strings.REQUIRED_FIELD_ERROR;
        }

        if (!formData.company.trim()) {
            newErrors.company = strings.REQUIRED_FIELD_ERROR;
        }
        
        if (formData.phone && !validatePhone(formData.phone)) {
            newErrors.phone = strings.INVALID_PHONE_ERROR;
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field: keyof ContactFormData, value: string) => {
        // Limit message length to 500 characters
        if (field === 'message' && value.length > 500) {
            return;
        }
        
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setIsSubmitting(true);
        setSubmitStatus('idle');
        
        try {
            // Send email using the email service
            const response = await sendContactEmail(formData);
            
            if (response.success) {
                setSubmitStatus('success');
                
                // Reset form after successful submission
                setFormData({
                    name: '',
                    email: '',
                    company: '',
                    phone: '',
                    message: ''
                });
            } else {
                setSubmitStatus('error');
                logger.error('Email sending failed:', response.error);
            }
            
        } catch (error) {
            logger.error('Error sending email:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="contact-form-wrapper">
            <form className="contact-us-form" onSubmit={handleSubmit}>
                    <div className="contact-us-header">
                        <h2>{strings.CONTACT_US_TITLE}</h2>
                        <p className="contact-us-description">
                        {strings.CONTACT_US_DESCRIPTION}
                        </p>
                    </div>
                    <div className="form-row">
                        <label>
                            {strings.NAME_LABEL} *
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                placeholder={strings.NAME_PLACEHOLDER}
                                className={errors.name ? 'input-error' : ''}
                            />
                            {errors.name && <span className="error-message">{errors.name}</span>}
                        </label>
                    </div>
                    
                    <div className="form-row">
                        <label>
                            {strings.EMAIL_LABEL} *
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                placeholder={strings.EMAIL_PLACEHOLDER}
                                className={errors.email ? 'input-error' : ''}
                            />
                            {errors.email && <span className="error-message">{errors.email}</span>}
                        </label>
                    </div>
                    
                    <div className="form-row">
                        <label>
                            {strings.COMPANY_LABEL} *
                            <input
                                type="text"
                                value={formData.company}
                                onChange={(e) => handleInputChange('company', e.target.value)}
                                placeholder={strings.COMPANY_PLACEHOLDER}
                                className={errors.company ? 'input-error' : ''}
                            />
                            {errors.company && <span className="error-message">{errors.company}</span>}
                        </label>
                    </div>
                    
                    <div className="form-row">
                        <label>
                            {strings.PHONE_LABEL}
                            <input
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                placeholder={strings.PHONE_PLACEHOLDER}
                                className={errors.phone ? 'input-error' : ''}
                            />
                            {errors.phone && <span className="error-message">{errors.phone}</span>}
                        </label>
                    </div>
                    
                    <div className="form-row">
                        <label>
                            {strings.MESSAGE_LABEL} *
                            <textarea
                                value={formData.message}
                                onChange={(e) => handleInputChange('message', e.target.value)}
                                placeholder={strings.MESSAGE_PLACEHOLDER}
                                rows={5}
                                maxLength={500}
                                className={errors.message ? 'input-error' : ''}
                            />
                            <div className="character-counter">
                               {formData.message.length}/500
                            </div>
                            {errors.message && <span className="error-message">{errors.message}</span>}
                        </label>
                    </div>
                    
                    {submitStatus === 'success' && (
                        <div className="success-message">
                            {strings.MESSAGE_SENT_SUCCESS}
                        </div>
                    )}
                    
                    {submitStatus === 'error' && (
                        <div className="error-message">
                            {strings.MESSAGE_SENT_ERROR}
                        </div>
                    )}
                    
                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={() => {
                                // Check where user came from
                                const fromProfile = location.state?.from === 'profile';
                                const fromRegister = location.state?.from === 'register';
                                
                                if (fromProfile) {
                                    navigate('/profile');
                                } else if (fromRegister) {
                                    navigate('/register');
                                } else if (onBack) {
                                    onBack();
                                } else {
                                    // Default: go back to previous page
                                    navigate(-1);
                                }
                            }}
                            className="btn-back"
                        >
                            {strings.BACK_BUTTON}
                        </button>
                        
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="btn-active"
                        >
                            {isSubmitting ? strings.SENDING_MESSAGE_BUTTON : strings.SEND_MESSAGE_BUTTON}
                        </button>
                    </div>
            </form>
        </div>
    );
}
